import PropTypes from 'prop-types';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Dropdown.module.scss';

export type DropdownItemType = {
	key: string;
	value: string;
};

export type DropdownType = {
	label?: string;
	data: Array<DropdownItemType>;
	selected: string;
	error?: string;
	onSelect: (key: string) => void;
	disabled?: boolean;
};

const Dropdown = ({
	label,
	data,
	selected,
	error,
	onSelect,
	disabled = false,
}: DropdownType) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedValue, setSelectedValue] = useState(
		data.find((item) => item.key === selected)?.value || '',
	);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const handleSelect = useCallback(
		(key: string, value: string) => {
			setSelectedValue(value);
			setIsOpen(false);
			onSelect(key);
		},
		[onSelect],
	);

	const handleClickOutside = useCallback((event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	useEffect(() => {
		setSelectedValue(data.find((item) => item.key === selected)?.value || '');
	}, [selected, data]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			if (!disabled) {
				setIsOpen(!isOpen);
			}
		}
	};

	const handleOptionKeyDown = (
		e: React.KeyboardEvent,
		key: string,
		value: string,
	) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleSelect(key, value);
		}
	};

	return (
		<div className={styles.dropdownContainer} ref={dropdownRef}>
			<div
				id="dropdown"
				className={`${styles.customDropdown} ${disabled ? styles.disabled : ''}`}
				onClick={() => {
					if (!disabled) {
						setIsOpen(!isOpen);
					}
				}}
				role="button"
				tabIndex={0}
				onKeyDown={handleKeyDown}
			>
				<div className={styles.selectedValue}>{selectedValue}</div>
				<div className={styles.chevron}>
					{isOpen ? (
						<svg
							className="w-5 h-5 text-[#333] dark:text-white"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							width="17"
							height="17"
							fill="none"
							viewBox="0 0 24 24"
						>
							<path
							stroke="#333"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m5 15 7-7 7 7"
							/>
						</svg>
					) : (
						<svg
							className="w-5 h-5 text-[#333] dark:text-white"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							width="17"
							height="17"
							fill="none"
							viewBox="0 0 24 24"
						>
							<path
								stroke="#333"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m19 9-7 7-7-7"
							/>
						</svg>
					)}
				</div>
			</div>
			{label && (
				<label className={`${styles.label}`} htmlFor="dropdown">
					{label}
				</label>
			)}
			<div
				className={`${styles.optionsContainer} ${isOpen ? styles.open : ''}`}
			>
				{data.map(({ key, value }) => (
					<div
						key={key}
						className={`${styles.option} ${key === selected ? styles.selected : ''}`}
						onClick={() => handleSelect(key, value)}
						title={value}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => handleOptionKeyDown(e, key, value)}
					>
						{value}
					</div>
				))}
			</div>
			{error && <p className={styles.error}>{error}</p>}
		</div>
	);
};

Dropdown.propTypes = {
	label: PropTypes.string,
	data: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
		}),
	).isRequired,
	selected: PropTypes.string.isRequired,
	error: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default Dropdown;
